/* eslint-disable jsx-a11y/role-supports-aria-props */
import React, { FC, ChangeEventHandler, FocusEventHandler } from 'react';
import { time } from 'uniqid';
import { Error } from '../Error';
import { useMedia } from '../util';
import { isEmpty } from 'lodash';
import { RADIO_BUTTON_VERTICAL_WIDTH } from '../constants';
import { showComponent } from '@trip-ux/common';

export type ToggleSelectorProps = {
  value: string;
  options: {
    label: string;
    value: string;
    ariaLabel?: string;
  }[];
  id?: string;
  disabled?: boolean;
  onChange: ChangeEventHandler<HTMLInputElement>;
  className?: string;
  name: string;
  error?: string;
  showError?: boolean;
  verticalWidth?: number;
  hasMobile?: boolean;
  isRequired?: boolean;
  ariaLabelledBy?: string;
  onBlur?: FocusEventHandler;
  onFocus?: FocusEventHandler;
  children?: React.ReactNode;
};

export const ToggleSelector: FC<ToggleSelectorProps> = ({
  value,
  options,
  id = time('toggle-'),
  disabled = false,
  onChange,
  className = '',
  name,
  error,
  showError = true,
  verticalWidth = RADIO_BUTTON_VERTICAL_WIDTH,
  onBlur = () => true,
  onFocus = () => true,
  hasMobile = true,
  isRequired,
  ariaLabelledBy,
  children
}) => {
  const isVertical = useMedia([`(max-width: ${verticalWidth}px)`], [true], false);
  return (
    <div className="toggle-selector">
      {showComponent(
        isEmpty(ariaLabelledBy),
        <label className="toggle-label" id={`${id}-label`} data-testid="legend">
          {children}
        </label>
      )}
      <div
        role="radiogroup"
        aria-required={isRequired}
        aria-invalid={!isEmpty(error)}
        aria-labelledby={ariaLabelledBy || `${id}-label`}
        className={className}
        id={id}
        data-testid={id}>
        <div className={`${hasMobile && isVertical ? 'vertical' : ''} toggle`}>
          {options?.map((option, idx) => (
            <div key={`options-${idx}`} className="toggle-element">
              <input
                value={option.value}
                name={name}
                disabled={disabled}
                onChange={onChange}
                type="radio"
                aria-label={option.ariaLabel}
                checked={option.value === value}
                id={`${id}-${option.value}`}
                className="toggle-input"
                onBlur={onBlur}
                onFocus={onFocus}
                data-testid={`toggle-input-${option.value}`}
                aria-describedby={`${id}-described`}
              />
              <label htmlFor={`${id}-${option.value}`} className={showError && !isEmpty(error) ? 'error' : ''}>
                {option.label}
              </label>
            </div>
          ))}
        </div>
        <Error errors={error} callout={false} id={`${id}-described`} />
      </div>
    </div>
  );
};

ToggleSelector.displayName = 'ToggleSelector';
export default ToggleSelector;
